import { numberParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { intersects, roundTo } from '../types/geometry.js';
import type { NormalizedRect } from '../types/geometry.js';
import { analyzeBackgroundSurface, type BackgroundCell } from '../quality/regions.js';
import { missingRequirementReason, notChecked, plainResult } from './support.js';

/**
 * «Тень на фоне».
 *
 * Two independent sources are combined:
 *   1. the localized `shadow` issues the background stage already found;
 *   2. a coarse grid over the background with the subject excluded, where a cell
 *      much darker than the median background counts as a shadow.
 *
 * Every shadow area becomes a `problemRegion`, so the overlay can point at the
 * exact spot. The check only ever says `warning`: a shadow behind the person is
 * a retake reason, not a formal violation.
 */
export const shadowBackgroundCheck: ValidationCheck = {
  id: 'shadow_background',
  title: 'Тень на фоне',
  description:
    'Ищет затемнённые участки фона рядом с человеком и отмечает их на фотографии.',
  kind: 'background',
  requires: ['background', 'pixels'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['background', 'pixels']);
    if (missing) return notChecked(ctx, shadowBackgroundCheck, missing, ctx.rule.severity);
    const background = ctx.background;
    const pixels = ctx.pixels;
    if (!background || !pixels) {
      return notChecked(
        ctx,
        shadowBackgroundCheck,
        'Недостаточно данных для поиска тени на фоне.',
        ctx.rule.severity,
      );
    }

    const shadowDelta = numberParam(ctx.rule.params, 'shadowDelta', 18);
    const minBackgroundCells = numberParam(ctx.rule.params, 'minBackgroundCells', 6);
    /**
     * The document profiles express the bounds as `maxShadowArea` /
     * `maxShadowScore`; the ratio/score form is also accepted directly.
     */
    const maxShadowArea = numberParam(
      ctx.rule.params,
      'maxShadowArea',
      numberParam(ctx.rule.params, 'maxShadowAreaRatio', 0.05),
    );
    const maxShadowScore = numberParam(
      ctx.rule.params,
      'maxShadowScore',
      numberParam(ctx.rule.params, 'maxGridShadowScore', 0.6),
    );

    if (!background.hasMask) {
      return notChecked(
        ctx,
        shadowBackgroundCheck,
        'Фон проанализировать не удалось: вокруг головы слишком мало видимого фона.',
        ctx.rule.severity,
      );
    }

    const surface = analyzeBackgroundSurface({
      buffer: pixels,
      subjectRect: background.subjectRect,
      ...(ctx.geometry ? { geometry: ctx.geometry } : {}),
    });

    const issueRects = background.issues
      .filter((issue) => issue.kind === 'shadow')
      .map((issue) => issue.rect);

    const darkCells: BackgroundCell[] =
      surface.cells.length >= minBackgroundCells && surface.referenceLuma > 0
        ? surface.cells.filter(
            (cell) => surface.referenceLuma - cell.meanLuma > shadowDelta,
          )
        : [];

    /**
     * A grid cell that overlaps an already-reported issue is the same shadow;
     * only report it once so the overlay does not stack duplicate markers.
     */
    const uniqueCells = darkCells.filter(
      (cell) => !issueRects.some((rect) => intersects(rect, cell.rect)),
    );

    const totalCellArea = surface.cells.reduce(
      (sum, cell) => sum + cell.rect.width * cell.rect.height,
      0,
    );
    const darkArea = darkCells.reduce((sum, cell) => sum + cell.rect.width * cell.rect.height, 0);
    const shadowAreaRatio = totalCellArea > 0 ? darkArea / totalCellArea : 0;

    const areaTrigger = shadowAreaRatio > maxShadowArea;
    const scoreTrigger = background.shadowScore > maxShadowScore;

    const problems: Array<{ id: string; rect: NormalizedRect; message: string }> = [
      ...issueRects.map((rect, index) => ({
        id: `shadow_background_issue_${index}`,
        rect,
        message: 'На фоне есть затемнённый участок.',
      })),
      ...uniqueCells.map((cell, index) => ({
        id: `shadow_background_cell_${index}`,
        rect: cell.rect,
        message: 'На фоне есть затемнённый участок.',
      })),
    ];

    /**
     * The background stage's own shadow score may fire without the coarse grid
     * isolating a single cell. The overlay still needs a rectangle, so the
     * darkest available cell — or, failing that, the subject neighbourhood —
     * becomes the marker.
     */
    if (problems.length === 0 && (areaTrigger || scoreTrigger)) {
      const darkest = [...surface.cells].sort((a, b) => a.meanLuma - b.meanLuma)[0];
      if (darkest) {
        problems.push({
          id: 'shadow_background_darkest_cell',
          rect: darkest.rect,
          message: 'На фоне есть затемнённый участок.',
        });
      }
    }

    const hasShadow = problems.length > 0 || areaTrigger || scoreTrigger;
    const maskCoverage = background.maskCoverage;
    const coverageScore = Math.max(0, Math.min(1, maskCoverage / 0.2));
    const confidence = Math.max(
      0.2,
      Math.min(1, 0.3 + coverageScore * 0.5 + Math.min(1, surface.cells.length / 24) * 0.2),
    );

    const insufficient =
      surface.backgroundPixels < 128 || surface.cells.length < minBackgroundCells;

    if (insufficient) {
      return notChecked(
        ctx,
        shadowBackgroundCheck,
        'Фон проанализировать не удалось: вокруг головы слишком мало видимого фона.',
        ctx.rule.severity,
      );
    }

    return plainResult({
      id: shadowBackgroundCheck.id,
      title: shadowBackgroundCheck.title,
      status: hasShadow ? 'warning' : 'pass',
      severity: ctx.rule.severity,
      value: roundTo(shadowAreaRatio, 4),
      unit: 'ratio',
      message: hasShadow ? '⚠ Тень на фоне' : '✅ Тени на фоне нет',
      requirement: 'Фон должен быть освещён ровно, без теней от человека и предметов.',
      fixability: 'retake',
      geometry: problems.map((problem) => ({
        type: 'rect' as const,
        coordinates: [
          roundTo(problem.rect.x, 4),
          roundTo(problem.rect.y, 4),
          roundTo(problem.rect.width, 4),
          roundTo(problem.rect.height, 4),
        ] as [number, number, number, number],
      })),
      problemRegions: problems.map((problem) => ({
        id: problem.id,
        rect: {
          x: roundTo(problem.rect.x, 4),
          y: roundTo(problem.rect.y, 4),
          width: roundTo(problem.rect.width, 4),
          height: roundTo(problem.rect.height, 4),
        },
        message: problem.message,
        severity: 'warning' as const,
      })),
      confidence: roundTo(confidence, 2),
      details: {
        shadowAreaRatio: roundTo(shadowAreaRatio, 4),
        darkCellCount: darkCells.length,
        issueCount: issueRects.length,
        referenceLuma: roundTo(surface.referenceLuma, 1),
        backgroundCells: surface.cells.length,
        maskCoverage,
        gridShadowScore: background.shadowScore,
        limits: { shadowDelta, minBackgroundCells, maxShadowArea, maxShadowScore },
        reasons: { areaTrigger, scoreTrigger },
        regions: problems.map((problem) => ({ id: problem.id, rect: problem.rect })),
        displayValue: `${roundTo(shadowAreaRatio * 100, 0).toFixed(0)} %`,
      },
    });
  },
};
